import { FunctionComponent, useMemo } from 'react';
import { Box, createStyles, makeStyles, Theme, Typography, useMediaQuery, useTheme } from '@material-ui/core';
import { Button } from 'components/UI';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faIdCardClip } from '@fortawesome/pro-regular-svg-icons';
import Client from '@spike/client-model';
import { wbp, reduceResolution } from 'Theme';
import clsx from 'clsx';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { ClientProfileOverview } from '@spike/client-model';

interface Props {
    clientData: Client;
    onClientCardClick: (clientId: number, petId?: number) => void;
    onBookNowClick: () => void;
    onBackButton: () => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        chatHeaderContainer: {
            width: '100%',
            height: '8%',
            display: 'flex',
            [theme.breakpoints.down(wbp)]: {
                padding: `${reduceResolution(5)}px`
            },
            [theme.breakpoints.up(wbp)]: {
                padding: '5px'
            },
            flexDirection: 'row',
            alignItems: 'center',
            borderBottom: '#D4D4D4 1px solid'
        },
        chatHeaderLeftContainer: {
            display: 'flex',
            width: '50%',
            flexDirection: 'row',
            alignItems: 'center'
        },
        chatHeaderRightContainer: {
            display: 'flex',
            width: '50%',
            flexDirection: 'row-reverse',
            alignItems: 'center'
        },
        chatHeaderTitle: {
            color: '#000',
            fontWeight: 600,
            [theme.breakpoints.down(wbp)]: {
                paddingLeft: `${reduceResolution(25)}px !important`,
                fontSize: `${reduceResolution(16)}px`
            },
            [theme.breakpoints.up(wbp)]: {
                paddingLeft: '25px !important',
                fontSize: '16px'
            }
        },
        nameRow: {
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexWrap: 'nowrap',
            minWidth: 0
        },
        nameText: {
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
        },
        owesPill: {
            color: '#CC444B',
            fontSize: 13,
            fontWeight: 500,
            borderRadius: 100,
            padding: '4px 10px',
            backgroundColor: '#FFE5E7',
            lineHeight: '20px',
            display: 'inline-flex',
            alignItems: 'center',
            position: 'relative',
            top: -2
        },
        chatHeaderItem: {
            cursor: 'pointer',
            [theme.breakpoints.down(wbp)]: {
                paddingRight: `${reduceResolution(15)}px`,
                paddingLeft: `${reduceResolution(15)}px`
            },
            [theme.breakpoints.up(wbp)]: {
                paddingRight: '15px',
                paddingLeft: '15px'
            }
        },
        chatBookNowButton: {
            [theme.breakpoints.down(wbp)]: {
                height: `${reduceResolution(37)}px`
            },
            [theme.breakpoints.up(wbp)]: {
                height: '37px'
            }
        },
        backButton: {
            cursor: 'pointer',
            paddingLeft: '12px'
        }
    })
);

const ChatHeader: FunctionComponent<Props> = props => {
    const classes = useStyles();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const profileOverview = useSelector<RootState, ClientProfileOverview | undefined>(
        state => state.clients.profileOverview
    );

    const parseMoney = (value: unknown): number => {
        if (typeof value === 'number') return value;
        if (typeof value !== 'string') return Number.NaN;
        return parseFloat(value.replace(/[^0-9.-]+/g, ''));
    };

    const getProfileCustomerId = (overview: ClientProfileOverview | undefined): number | undefined => {
        const anyOverview = overview as any;
        return (
            anyOverview?.customer?.id ??
            anyOverview?.customerId ??
            anyOverview?.customer?.customerId ??
            anyOverview?.customer?.clientId
        );
    };

    const clientOwes = useMemo(() => {
        if (!props.clientData?.id || !profileOverview) return false;

        const overviewCustomerId = getProfileCustomerId(profileOverview);
        if (overviewCustomerId != null && overviewCustomerId !== props.clientData.id) return false;

        const debt = (profileOverview as any)?.outstandingInvoices?.invoiceDebt;
        const amount = parseMoney(debt);
        return !Number.isNaN(amount) && amount > 0;
    }, [props.clientData?.id, profileOverview]);

    return (
        <Box className={classes.chatHeaderContainer}>
            <Box className={classes.chatHeaderLeftContainer}>
                {isMobile && (
                    <FontAwesomeIcon
                        icon={faArrowLeft}
                        size="lg"
                        onClick={() => props.onBackButton && props.onBackButton()}
                        className={classes.backButton}
                    />
                )}
                <Typography className={clsx(classes.chatHeaderTitle, classes.chatHeaderItem)}>
                    <span className={classes.nameRow}>
                        <span className={classes.nameText}>
                            {props.clientData?.firstName} {props.clientData?.lastName}
                        </span>
                        {clientOwes && <span className={classes.owesPill}>Owes</span>}
                    </span>
                </Typography>
            </Box>
            <Box className={classes.chatHeaderRightContainer}>
                <FontAwesomeIcon
                    onClick={() => props.onClientCardClick(props.clientData.id!)}
                    className={classes.chatHeaderItem}
                    icon={faIdCardClip}
                />
                <Button
                    onClick={props.onBookNowClick}
                    className={clsx(classes.chatBookNowButton, classes.chatHeaderItem)}
                    size="small"
                    label="Book Now"
                    color="green"
                />
            </Box>
        </Box>
    );
};

export default ChatHeader;
